/**
 * DemoModeBadge Component v2.3
 * 
 * Always-visible badge when app is in Demo Link mode.
 * Shows:
 * - "Demo Mode" indicator
 * - Persona being viewed
 * - Time remaining until expiry
 * - Exit demo button
 */

import React, { useState, useEffect } from 'react';
import { useDemoLink } from '../../contexts/DemoLinkContext';
import { PERSONA_LABELS } from '../../guided/guidedCopyMatrix';
import { X, Clock, Eye, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export const DemoModeBadge = () => {
  const { isDemoMode, demoConfig, exitDemoMode } = useDemoLink();
  const [timeRemaining, setTimeRemaining] = useState('');

  // Update time remaining
  useEffect(() => {
    if (!isDemoMode || !demoConfig?.expiresAt) return;

    const updateTime = () => {
      const now = Date.now();
      const expiry = new Date(demoConfig.expiresAt).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining(`${minutes}m`);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [isDemoMode, demoConfig?.expiresAt]);

  if (!isDemoMode) return null;

  return (
    <div 
      className={cn(
        "fixed top-0 left-0 right-0 z-[100]",
        "bg-primary text-primary-foreground",
        "py-2 px-4",
        "flex items-center justify-center gap-4",
        "text-[12px] font-medium"
      )}
      data-testid="demo-mode-badge"
    >
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4" />
        <span>Simulation Environment</span>
      </div>
      
      <span className="text-primary-foreground/70">|</span>
      
      <div className="flex items-center gap-1">
        <span className="text-primary-foreground/70">Viewing as:</span>
        <span className="font-semibold">
          {PERSONA_LABELS[demoConfig?.persona] || 'Client'}
        </span>
      </div>
      
      <span className="text-primary-foreground/70">|</span>
      
      <div className="flex items-center gap-1">
        <Lock className="h-3 w-3" />
        <span className="text-primary-foreground/70">Read-only</span>
      </div>
      
      <span className="text-primary-foreground/70">|</span>
      
      <div className="flex items-center gap-1">
        <Clock className="h-3 w-3" />
        <span>{timeRemaining}</span>
      </div>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={exitDemoMode}
        className="h-6 px-2 text-[11px] ml-4 bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground"
        data-testid="exit-demo-btn"
      >
        <X className="h-3 w-3 mr-1" />
        Exit Demo
      </Button>
    </div>
  );
};

export default DemoModeBadge;
